import { createTheme, alpha } from "@material-ui/core/styles";
import { Shadows } from "@material-ui/core/styles/shadows";

declare module "@material-ui/core/styles/createPalette" {
  interface Palette {
    decorative: {
      purple: string,
      blue: string,
      green: string,
      red: string,
      yellow: string,
      pink: string,
      brown: string,
      orange: string,
      darkorange: string,
      darkbrown: string,
      darkblue: string,
      darkpink: string,
      darkgreen: string,
      darkyellow: string,
    };
  }
}

const original = createTheme();
const customShadows: Shadows = [...original.shadows];
const theme = {
  palette: {
    primary: {
      50: "#EBF4FE",
      300: "#90C0F9",
      500: "#0E5AE1",
      700: "#0242B4",
      900: "#00328C",
    },
    grey: {
      50: "#F8F9F9",
      100: "#E7E7E8",
      200: "#D8D9DB",
      300: "#CACBCD",
      400: "#BBBDBF",
      600: "#808285",
      700: "#636466",
      800: "#454648",
      900: "#282829",
    },
    success: {
      50: "#F1F8F5",
      300: "#7EB59F",
      500: "#377D61",
    },
    warning: {
      50: "#FFFAEC",
      300: "#FECE40",
      500: "#E5A803",
    },
    critical: {
      50: "#FEF5F2",
      300: "#EE9880",
      500: "#DD3100",
      700: "#B31100",
      900: "#660000",
    },
    purple: {
      50: "#F9F2FA",
      300: "#CE93D8",
      500: "#9C27B0",
    },
    decorative: {
      purple: "#e8ddff",
      blue: "#D3E2EC",
      green: "#B5E7B1",
      red: "#FFB8B8",
      yellow: "#FFF8B5",
      pink: "#FFEBEB",
      brown: "#DFCFBD",
      orange: "#FFE4C3",
      darkorange: "#C86868",
      darkbrown: "#775B3B",
      darkblue: "#1B4D72",
      darkpink: "#DA5EBF",
      darkgreen: "#589852",
      darkyellow: "#DE9947",
    }
  },
  typography: {
    fontFamily: "system-ui, sans-serif",
    h1: {
      color: "#282829",
      fontSize: "2.5rem",
      fontWeight: 500,
      lineHeight: "3rem",
    },
    h2: {
      color: "#282829",
      fontSize: "2rem",
      fontWeight: 600,
      lineHeight: "2.5rem",
    },
    h3: {
      color: "#282829",
      fontSize: "1.5rem",
      fontWeight: 500,
      lineHeight: "1.75rem",
    },
    h4: {
      color: "#282829",
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: "1.5rem",
    },
    h5: {
      color: "#282829",
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: "1.5rem",
    },
    h6: {
      color: "#282829",
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: "1.25rem",
    },
    subtitle1: {
      color: "#282829",
      fontSize: "0.75rem",
      fontWeight: 700,
      lineHeight: "1rem",
    },
    body1: {
      color: "#282829",
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.25rem",
    },
    body2: {
      color: "#282829",
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: "1.25rem",
    },
    subtitle2: {
      color: "#282829",
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: "1rem",
    },
  },
  spacing: (factor) => {
    const spacingValues = {
      0: "0rem",
      0.25: "0.0625rem",
      0.5: "0.125rem",
      1: "0.25rem",
      2: "0.5rem",
      3: "0.75rem",
      4: "1rem",
      5: "1.25rem",
      6: "1.5rem",
      7: "2rem",
      8: "2.5rem",
      9: "3rem",
      10: "4rem",
    };

    return spacingValues[factor];
  },
  shadows: customShadows,
};
theme.shadows[1] = "0px -2px 2px 0px rgba(0, 0, 0, 0.15)";
theme.shadows[2] = "0px 0px 0px 3px rgba(144, 192, 249, 1)";
theme.shadows[3] = "0px 1px 2px 0px rgba(0, 0, 0, 0.15)";
theme.shadows[4] = "0px 1px 3px 0px rgba(0, 0, 0, 0.25)";
theme.shadows[5] = "0px 1px 10px 0px rgba(0, 0, 0, 0.25)";
theme.shadows[6] = "0px 26px 80px 0px rgba(0, 0, 0, 0.25)";

const suraTheme = createTheme({
  ...theme,
  props: {
    MuiButton: {
      disableRipple: true,
    },
  },
  overrides: {
    MuiTypography: {
      root: {
        fontFamily: "system-ui, sans-serif",
        color: theme.palette.grey[900],
      }
    },
    MuiButton: {
      root: {
        borderRadius: "0.25rem",
        textTransform: "none",
        fontFamily: "system-ui, sans-serif",
        fontSize: "0.875rem",
        fontWeight: 400,
        height: "2.25rem",
        padding: `0 ${theme.spacing(4)}`,
        "&$focusVisible": {
          boxShadow: theme.shadows[2],
        },
      },
      sizeSmall: {
        height: "1.75rem",
        padding: `0 ${theme.spacing(3)}`,
      },
      sizeLarge: {
        height: "2.75rem",
        padding: `0 ${theme.spacing(6)}`,
      },
      outlined: {
        borderColor: theme.palette.grey[800],
        color: theme.palette.grey[800],
        backgroundColor: "#FFFFFF",
        "&:hover": {
          backgroundColor: theme.palette.grey[50],
        },
        "&:active": {
          backgroundColor: theme.palette.grey[100],
        },
        "&.Mui-disabled": {
          backgroundColor: "#FFFFFF",
          borderColor: theme.palette.grey[200],
          color: theme.palette.grey[500],
        },
      },
      text: {
        color: theme.palette.primary[500],
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: theme.palette.primary[50],
          color: theme.palette.primary[500],
          "& .MuiButton-label": {
            textDecoration: "underline",
            textUnderlineOffset: "0.125rem",
          },
        },
        "&:active": {
          backgroundColor: theme.palette.primary[50],
          color: theme.palette.primary[500],
          "& .MuiButton-label": {
            textDecoration: "underline",
            textUnderlineOffset: "0.125rem",
          },
        },
        "&.Mui-disabled": {
          color: theme.palette.grey[400],
          backgroundColor: "transparent",
          "& .MuiButton-label": {
            textDecoration: "underline",
            textUnderlineOffset: "0.125rem",
          },
        },
      },
      contained: {
        "&$focusVisible": {
          boxShadow: theme.shadows[2],
        },
      },
      containedPrimary: {
        backgroundColor: theme.palette.primary[500],
        color: "#FFFFFF",
        boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        "&:hover": {
          backgroundColor: theme.palette.primary[700],
          boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        },
        "&:active": {
          backgroundColor: theme.palette.primary[900],
          boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        },
        "&.Mui-disabled": {
          backgroundColor: theme.palette.grey[300],
          color: theme.palette.grey[700],
          boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        },
      },
      containedSecondary: {
        backgroundColor: theme.palette.critical[500],
        color: "#FFFFFF",
        boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        "&:hover": {
          backgroundColor: theme.palette.critical[700],
          boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        },
        "&:active": {
          backgroundColor: theme.palette.critical[900],
          boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        },
        "&.Mui-disabled": {
          backgroundColor: theme.palette.grey[300],
          color: theme.palette.grey[700],
          boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.24)}`,
        },
      },
    },
    MuiChip: {
      root: {
        height: '24px',
        backgroundColor: theme.palette.success[50],
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
        fontSize: '14px',
        lineHeight: '25px',
        margin: '5px',
        fontFamily: 'system-ui, sans-serif',
        '&:hover, &:focus': {
          backgroundColor: theme.palette.success[50],
        }
      },
      deletable: {
        backgroundColor: theme.palette.grey[50],
        border: '1px solid #BBBDBF',
        boxShadow: 'none',
      },
      deleteIcon: {
        height: '12px',
        width: '12px',
        color: theme.palette.grey[800],
        marginRight: '8px',
      }
    },
    MuiSelect: {
      select: {
        fontSize: 14,
        fontWeight: 600,
        fontFamily: 'system-ui',
        backgroundColor: 'transparent',
        padding: '8px 10px',
        paddingRight: 32,
        '&:focus': {
          backgroundColor: 'transparent',
        }
      }
    },
    MuiInput: {
      root: {
        fontSize: "1rem",
        color: theme.palette.grey[900],
        borderRadius: 4,
        background: "white",
        border: `1px solid ${theme.palette.grey[600]}`,
        '&.Mui-error': {
          borderColor: theme.palette.critical[500]
        },
        '&.Mui-focused': {
          borderColor: theme.palette.primary[500],
          boxShadow: theme.shadows[2],
          '&.Mui-error': {
            borderColor: theme.palette.critical[500]
          }
        },
        '&.MuiInput-multiline': {
          alignItems: 'flex-start',
          padding: 0,
        }
      },
      input: {
        padding: '0.625rem 0.5rem',
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: '0.875rem',
        color: theme.palette.grey[800],
        '&.Mui-error': {
          color: theme.palette.grey[800]
        },
        '&.Mui-disabled': {
          color: theme.palette.grey[800]
        },
        '& + .MuiInput-formControl': {
          marginTop: 24
        }
      }
    },
    MuiFormLabel: {
      root: {
        '&.Mui-focused': {
          color: theme.palette.grey[800]
        }
      }
    },
    MuiFormHelperText: {
      root: {
        fontSize: '0.875rem',
        marginTop: '4px',
        '&.Mui-error': {
          color: theme.palette.critical[500]
        },
      }
    },
    MuiLink: {
      root: {
        '&.MuiLink-underlineHover': {
          textDecoration: 'underline'
        }
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: "#fff",
        boxShadow: theme.shadows[5],
        fontSize: theme.typography.subtitle2.fontSize,
        padding: "0.5rem",
        fontWeight: 400,
        color: theme.palette.grey[900]
      }
    },
  },
});

export default suraTheme;
